import {Button, Checkbox, CircularProgress, Pagination} from "@mui/material";
import PageSizeSelector from "../../common/PageSizeSelector";
import {PaginationWrapper} from "../../common/TableContainer.styled";
import React, {useCallback, useEffect, useState} from 'react';
import {
    EmptyState,
    EmptyStateText,
    FileSelectionItemsGridContainer,
    FileSelectionPaginationWrapper,
    FilesGrid,
    FilesListWrapper,
    PaginationLeft,
    PaginationRight,
    SourceActions,
    SourceCheckboxLabel,
    SourceHeader,
    SourceName,
    SourceSection,
    StyledPageSizeLabel
} from "./FileSelectionItemsGrid.styled";
import FileCard from "../FileCard";
import {OptionsButton} from "../FileCard/FileCard.styled";
import {ReactComponent as MoreIcon} from '../../../assets/icons/more.svg';
import {ReactComponent as EditIcon} from '../../../assets/icons/edit.svg';
import {ReactComponent as PlusIcon} from '../../../assets/icons/plus_2.svg';
import {ReactComponent as TrashIcon} from '../../../assets/icons/trash.svg';
import {DocumentDataResponse} from "../../../models/collections.models";
import {useTranslation} from "react-i18next";
import ConfirmationDialog from "../../dialogs/ConfirmationDialog";
import GenericInputDialog from "../../dialogs/InputDialog";
import AddDocumentDialog from "../../data-sources/AddDocumentDialog";
import {ContextMenu, ContextMenuItem} from "../../data-sources/DataSourceTable.styled";
import {useCollectionActions} from "../../../hooks/useCollectionActions";
import {useConversation} from "../../../contexts/ConversationContext";
import {DataSource} from "../../../models/models";
import {GroupPaginationState} from "../useGroupDocuments";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import {theme} from "../../../theme";
import SearchInput from "../../common/SearchInput";
import {useSnackbar} from "../../../contexts/SnackbarContext";


interface FileSelectionItemsGridProps {
    source: DataSource;
    itemsByGroup:  Record<string, DocumentDataResponse[]>;
    isLoadingGroupItems: boolean;
    pageSizeOptions: number[];
    onLoadGroupItems: (groupId: number, currentPage: number, pageSize: number, search?: string) => void;
    getGroupPagination: (groupId: number) => GroupPaginationState;
    onPageSizeChange: (groupId: number, pageSize: number) => void;
    onCurrentPageChange: (groupId: number, newPage: number) => void;
    onSearchChange: (groupId: number, newSearch: string) => void;
    onDataChanged?: () => void;
    hideSelectAllItems?: boolean;
}

const FileSelectionItemsGrid = ({
    source,
    itemsByGroup,
    isLoadingGroupItems,
    pageSizeOptions,
    onLoadGroupItems,
    getGroupPagination,
    onPageSizeChange,
    onCurrentPageChange,
    onSearchChange,
    onDataChanged,
    hideSelectAllItems,
}: FileSelectionItemsGridProps) => {
    const {t} = useTranslation();
    const {showSnackbar} = useSnackbar();

    const {state, updateDocumentsFromUser, updateSourcesFromUser} = useConversation();
    const {selectedGroupIds, selectedDocIds, selectedWholeCollectionIds} = state;

    const {page, pageSize, totalPages, search} = getGroupPagination(source.id);

    const items: Array<DocumentDataResponse> = itemsByGroup[String(source.id)] || [];

    const allSelectedItems = selectedWholeCollectionIds.includes(source.id) || (selectedDocIds.length > 0 && selectedDocIds.length === source.amountOfDocuments);
    const someSelectedItems = !allSelectedItems && (selectedDocIds.length > 0 && selectedDocIds.length < source.amountOfDocuments);

    const [searchInput, setSearchInput] = useState(search);
    const debouncedSearch = useDebouncedValue(searchInput, 300);

    useEffect(() => {
        setSearchInput(search);
    }, [source.id]);

    useEffect(() => {
        if (debouncedSearch !== search) {
            onSearchChange(source.id, debouncedSearch);
        }
    }, [debouncedSearch, source.id, search, onSearchChange]);


    const handleSourceDeleted = useCallback((sourceId: number) => {
        const newGroupIds = selectedGroupIds.filter(id => id !== sourceId);
        const removedDocIds = new Set((itemsByGroup[String(sourceId)] || []).map(doc => doc.id));
        const newDocIds = selectedDocIds.filter(id => !removedDocIds.has(id));

        void updateSourcesFromUser(newGroupIds, newDocIds);
    }, [itemsByGroup, selectedDocIds, selectedGroupIds, updateSourcesFromUser]);

    const handleDocumentDeleted = useCallback((documentId: number) => {
        const newDocIds = selectedDocIds.filter(id => id !== documentId);

        void updateDocumentsFromUser(newDocIds, []);
    }, [selectedDocIds, updateDocumentsFromUser]);

    const handleRefreshSource = useCallback((sourceId: number) => {
        onLoadGroupItems(sourceId, page, pageSize, search);
    }, [onLoadGroupItems, page, pageSize, search]);

    const {
        sourceContextMenu,
        documentContextMenu,
        editNameDialog,
        addDocumentDialog,
        deleteSourceDialog,
        deleteDocumentDialog,
        openSourceContextMenu,
        closeSourceContextMenu,
        openDocumentContextMenu,
        closeDocumentContextMenu,
        openEditNameDialog,
        openAddDocumentDialog,
        openDeleteSourceDialog,
        openDeleteDocumentDialog,
        closeEditNameDialog,
        closeDeleteSourceDialog,
        closeDeleteDocumentDialog,
        updateSourceName,
        deleteSource,
        addDocuments,
        deleteDocument,
    } = useCollectionActions({
        onDataChanged,
        onSourceDeleted: handleSourceDeleted,
        onDocumentDeleted: handleDocumentDeleted,
        onRefreshSource: handleRefreshSource,
    });

    const handleFileToggle = useCallback((docId: number, sourceId: number) => {
        const isWholeCollectionSelected = selectedWholeCollectionIds.includes(sourceId);

        if (isWholeCollectionSelected) {
            const newWholeCollectionIds = selectedWholeCollectionIds.filter(id => id !== sourceId);
            const sourceDocIds = new Set((itemsByGroup[String(sourceId)] || []).map(doc => doc.id));
            const newDocIds = selectedDocIds.filter(id => !sourceDocIds.has(id));

            void updateDocumentsFromUser(newDocIds, newWholeCollectionIds);
            showSnackbar(t('fileSelection.deselectAllInfo'), "info");
            return;
        }

        const isCurrentlySelected = selectedDocIds.includes(docId);

        if (isCurrentlySelected) {
            void updateDocumentsFromUser(
                selectedDocIds.filter(id => id !== docId),
                [],
            );
        } else {
            const newDocIds = [...selectedDocIds, docId];
            const isWholeCollectionSelected = newDocIds.length > 0 && newDocIds.length === source.amountOfDocuments;

            void updateDocumentsFromUser(
                newDocIds,
                isWholeCollectionSelected ? [sourceId] : [],
            );
        }
    }, [itemsByGroup, selectedDocIds, selectedWholeCollectionIds, source, t, updateDocumentsFromUser]);

    const handleSelectAllInSource = useCallback((sourceId: number) => {
        const docs = itemsByGroup[String(sourceId)] || [];
        const docIds = new Set(docs.map(d => d.id));
        const newDocIds = selectedDocIds.filter(id => !docIds.has(id));

        void updateDocumentsFromUser(newDocIds, [...selectedWholeCollectionIds, sourceId]);
    }, [itemsByGroup, selectedDocIds, selectedWholeCollectionIds, updateDocumentsFromUser]);

    const handleDeselectAllInSource = useCallback((sourceId: number) => {
        const docs = itemsByGroup[String(sourceId)] || [];
        const docIds = new Set(docs.map(d => d.id));

        const newDocIds = selectedDocIds.filter(id => !docIds.has(id));
        const newWholeCollectionIds = selectedWholeCollectionIds.filter(id => id !== sourceId);

        void updateDocumentsFromUser(newDocIds, newWholeCollectionIds);
    }, [itemsByGroup, selectedDocIds, selectedWholeCollectionIds, updateDocumentsFromUser]);

    const handleDocumentOptionsClick = useCallback((
        event: React.MouseEvent,
        document: DocumentDataResponse,
        sourceId: number
    ) => {
        openDocumentContextMenu(event, document, sourceId);
    }, [openDocumentContextMenu]);

    const handleDocumentCreated = useCallback(() => {
        if (addDocumentDialog) {
            onLoadGroupItems(Number(addDocumentDialog.sourceId), page, pageSize, search);
        }

        void addDocuments();
    }, [page, pageSize, search, addDocumentDialog, onLoadGroupItems, addDocuments]);

    const handleChangeSearchInput = (value: string) => {
        setSearchInput(value);
    };

    return (
        <FileSelectionItemsGridContainer>
            <SourceSection>
                <SourceHeader>
                    <SourceName>{source.name}</SourceName>

                    <SearchInput onChange={handleChangeSearchInput} value={searchInput} />

                    <SourceActions>
                        {hideSelectAllItems && items.length > 0 && (
                            <SourceCheckboxLabel>
                                <Checkbox
                                    checked={allSelectedItems}
                                    indeterminate={someSelectedItems}
                                    onChange={() => allSelectedItems
                                        ? handleDeselectAllInSource(source.id)
                                        : handleSelectAllInSource(source.id)}
                                    inputProps={{
                                        'aria-label': t('fileSelection.selectAllInSource', {
                                            sourceName: source.name,
                                            defaultValue: `Zaznacz lub odznacz wszystkie dokumenty w kolekcji ${source.name}`
                                        }) as string
                                    }}
                                />
                                {t('fileSelection.selectAll')}
                            </SourceCheckboxLabel>
                        )}

                        {!hideSelectAllItems && items.length > 0 && (
                            <Button
                                size="small"
                                onClick={() => allSelectedItems ? handleDeselectAllInSource(source.id) : handleSelectAllInSource(source.id)}
                                aria-label={allSelectedItems ? t('fileSelection.clearSelection') as string : t('fileSelection.selectAll') as string}
                            >
                                {allSelectedItems ? t('fileSelection.clearSelection') : t('fileSelection.selectAll')}
                            </Button>
                        )}

                        <OptionsButton
                            size="small"
                            color="secondary"
                            style={{width: 26}}
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                openSourceContextMenu(e, source);
                            }}
                        >
                            <MoreIcon/>
                        </OptionsButton>
                    </SourceActions>
                </SourceHeader>

                {(isLoadingGroupItems && items.length === 0) && (
                    <EmptyState>
                        <CircularProgress size={24} color="primary"/>
                    </EmptyState>
                )}

                {(isLoadingGroupItems && items.length > 0) && (
                    <EmptyState>
                        <CircularProgress size={24} color="primary"/>
                    </EmptyState>
                )}

                {(items.length === 0 && !isLoadingGroupItems) && (
                    <EmptyState>
                        <EmptyStateText>
                            {t('fileSelection.noFiles', {defaultValue: 'Brak plików w tym źródle'})}
                        </EmptyStateText>
                    </EmptyState>
                )}

                {(!isLoadingGroupItems && items.length > 0) && (
                    <FilesListWrapper>
                        <FilesGrid>
                            {items.map(doc => (
                                <FileCard
                                    key={doc.id}
                                    document={doc}
                                    selected={allSelectedItems || selectedDocIds.includes(doc.id)}
                                    onToggle={() => handleFileToggle(doc.id, source.id)}
                                    onOptionsClick={(e) => handleDocumentOptionsClick(e, doc, source.id)}
                                />
                            ))}
                        </FilesGrid>

                        <FileSelectionPaginationWrapper>
                            <PaginationLeft>
                                <StyledPageSizeLabel>{t('fileSelection.display') + ':'}</StyledPageSizeLabel>
                                <PageSizeSelector
                                    options={pageSizeOptions}
                                    selectedSize={pageSize}
                                    onChange={(newSize) => onPageSizeChange(source.id, newSize)}
                                />
                            </PaginationLeft>

                            <PaginationWrapper>
                                <PaginationRight>
                                    <Pagination
                                        count={totalPages}
                                        page={page + 1}
                                        onChange={(_, newPage) => onCurrentPageChange(source.id, newPage)}
                                        shape="rounded"
                                        color="primary"
                                    />
                                </PaginationRight>
                            </PaginationWrapper>
                        </FileSelectionPaginationWrapper>
                    </FilesListWrapper>
                )}
            </SourceSection>


            <ContextMenu
                open={sourceContextMenu !== null}
                onClose={closeSourceContextMenu}
                anchorReference="anchorPosition"
                anchorPosition={
                    sourceContextMenu !== null
                        ? {top: sourceContextMenu.mouseY, left: sourceContextMenu.mouseX}
                        : undefined
                }
            >
                {sourceContextMenu && [
                    hideSelectAllItems ? (
                        <ContextMenuItem key="addDocument" onClick={() => openAddDocumentDialog(sourceContextMenu.source)}>
                            <PlusIcon style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                            {t('fileSelection.addDocuments')}
                        </ContextMenuItem>
                    ) : null,
                    <ContextMenuItem key="edit" onClick={() => openEditNameDialog(sourceContextMenu.source)}>
                        <EditIcon style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                        {t('fileSelection.editName')}
                    </ContextMenuItem>,
                    <ContextMenuItem
                        key="delete"
                        onClick={() => openDeleteSourceDialog(sourceContextMenu.source)}
                        style={{color: theme.palette.error.main}}
                    >
                        <TrashIcon style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                        {t('fileSelection.deleteSource')}
                    </ContextMenuItem>,
                ]}
            </ContextMenu>

            <ContextMenu
                open={documentContextMenu !== null}
                onClose={closeDocumentContextMenu}
                anchorReference="anchorPosition"
                anchorPosition={
                    documentContextMenu !== null
                        ? {top: documentContextMenu.mouseY, left: documentContextMenu.mouseX}
                        : undefined
                }
            >
                {documentContextMenu && [
                    <ContextMenuItem
                        key="delete"
                        onClick={() => openDeleteDocumentDialog(documentContextMenu.document, documentContextMenu.sourceId)}
                        style={{color: theme.palette.error.main}}
                    >
                        <TrashIcon style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                        {t('fileSelection.deleteDocument')}
                    </ContextMenuItem>,
                ]}
            </ContextMenu>

            {addDocumentDialog && (
                <AddDocumentDialog
                    open={true}
                    onClose={() => addDocuments()}
                    dataSourceId={addDocumentDialog.sourceId}
                    onDocumentCreated={handleDocumentCreated}
                />
            )}

            {editNameDialog && (
                <GenericInputDialog
                    open={true}
                    title={t('fileSelection.editNameTitle') as string}
                    label={t('fileSelection.editNameLabel') as string}
                    required
                    initialValue={editNameDialog.currentName}
                    onClose={closeEditNameDialog}
                    onConfirm={updateSourceName}
                />
            )}

            {deleteSourceDialog && (
                <ConfirmationDialog
                    open={true}
                    onClose={closeDeleteSourceDialog}
                    onConfirm={deleteSource}
                    title={t('fileSelection.deleteSourceTitle') as string}
                    message={<>{t('fileSelection.deleteSourceMessage')} &ldquo;{deleteSourceDialog.source.name}&rdquo;?</>}
                    confirmButtonText={t('fileSelection.deleteButton') as string}
                    cancelButtonText={t('fileSelection.cancelButton') as string}
                />
            )}

            {deleteDocumentDialog && (
                <ConfirmationDialog
                    open={true}
                    onClose={closeDeleteDocumentDialog}
                    onConfirm={deleteDocument}
                    title={t('fileSelection.deleteDocumentTitle') as string}
                    message={<>{t('fileSelection.deleteDocumentMessage')} &ldquo;{deleteDocumentDialog.document.name}&rdquo;?</>}
                    confirmButtonText={t('fileSelection.deleteButton') as string}
                    cancelButtonText={t('fileSelection.cancelButton') as string}
                />
            )}

        </FileSelectionItemsGridContainer>
    );
};

export default FileSelectionItemsGrid;